import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SearchIcon from "@mui/icons-material/Search";
import { useProducts } from "../../../Context/ProductsContext";
import { useCart } from "../../../Context/CartContext";
import { getImageUrl } from "../../../services/indexMediaBaseUrl";
import { saveProduct } from "../../../services/UserApi/UserApi";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CategoriesHorizontallyCompo from "./CategoriesHorizontallyCompo";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import ProductDialog from "./ProductDialog";
import AddToCartDialog from "../../../components/AddToCartDialog";

const Products = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // products from context
  const {
    grouped: ctxGrouped,
    flatProducts,
    loading: ctxLoading,
    error: ctxError,
    refresh,
  } = useProducts();

  useEffect(() => {
		document.title = 'DIDA MAX — Products';
	}, []);

  const groups = ctxGrouped || {};

  // modal (hover) - تركته زي ما هو عندك
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);

  // cart
  const { addToCart } = useCart();
  const [addCartOpen, setAddCartOpen] = useState(false);
  const [addCartProduct, setAddCartProduct] = useState(null);

  // category filter
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const handleHoverStart = (product) => {
    setModalProduct(product || null);
    setModalOpen(true);
  };

  const handleHoverEnd = () => {
    // no-op (بتقفلي من ال Dialog)
  };

  const { t, i18n } = useTranslation("products");

  // language / direction
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";
  const dir = isAr ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const getProductName = (p) =>
    isAr ? p.name_ar || p.name_en : p.name_en || p.name_ar;

  const getCategoryLabel = (groupKey, firstProduct) => {
    if (firstProduct && firstProduct.categroy) {
      return isAr
        ? firstProduct.categroy.name_ar
        : firstProduct.categroy.name_en;
    }
    return String(groupKey);
  };

  const getProductImage = (p) =>
    getImageUrl(
      p.main_photo || (p.images && p.images[0] && p.images[0].image) || "",
    );

  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const currencies = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId)
      entry = currencies.find(
        (c) => String(c.country_id) === String(countryId),
      );
    if (!entry && currencies.length) entry = currencies[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback
        ? {
            price: String(product.price || ""),
            price_after_discount: product.price_after_discount
              ? String(product.price_after_discount)
              : null,
          }
        : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  // save product state
  const [savingIds, setSavingIds] = useState([]);
  const [savedIds, setSavedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("savedProductIds") || "[]");
    } catch {
      return [];
    }
  });

  const persistSavedIds = (ids) => {
    try {
      localStorage.setItem("savedProductIds", JSON.stringify(ids));
    } catch {}
  };

  const handleSaveProduct = async (product) => {
    const userRaw = localStorage.getItem("user");
    if (!userRaw) {
      navigate("/login");
      return;
    }

    const pid = product?.id;
    if (!pid) return;
    if (savingIds.includes(pid)) return;

    setSavingIds((s) => [...s, pid]);
    try {
      await saveProduct(pid);
      setSavedIds((s) => {
        const next = Array.from(new Set([...s, pid]));
        persistSavedIds(next);
        return next;
      });
    } catch (err) {
      console.error("saveProduct error:", err);
    } finally {
      setSavingIds((s) => s.filter((id) => id !== pid));
    }
  };

  const handleOpenAddToCart = (product) => {
    setAddCartProduct(product || null);
    setAddCartOpen(true);
  };

  const handleCloseAddToCart = () => {
    setAddCartOpen(false);
    setAddCartProduct(null);
  };

  // filter
  const allProducts = Object.values(groups || {}).flat();
  const qTerm = (searchQuery || "").trim().toLowerCase();

  const matchedProducts =
    selectedCategoryId || qTerm
      ? allProducts.filter((p) => {
          const inCategory = selectedCategoryId
            ? String(p.categroy?.id || p.category_id || "") ===
              String(selectedCategoryId)
            : true;
          if (!inCategory) return false;

          if (qTerm) {
            const nameAr = String(p.name_ar || "").toLowerCase();
            const nameEn = String(p.name_en || "").toLowerCase();
            const descAr = String(p.desc_ar || "").toLowerCase();
            const descEn = String(p.desc_en || "").toLowerCase();
            return (
              nameAr.includes(qTerm) ||
              nameEn.includes(qTerm) ||
              descAr.includes(qTerm) ||
              descEn.includes(qTerm)
            );
          }
          return true;
        })
      : null;

  const clearSearch = () => {
    setSearchQuery("");
  };

  if (ctxLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.default",
        }}
        dir={dir}
      >
        <CircularProgress sx={{ color: "primary.main" }} size={56} />
      </Box>
    );
  }

  if (ctxError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.default",
        }}
        dir={dir}
      >
        <Typography color="error" variant="h6" fontWeight={700}>
          {t("error_loading_products", {
            defaultValue: isAr
              ? "حدث خطأ في تحميل المنتجات"
              : "Error loading products",
          })}
        </Typography>
      </Box>
    );
  }

  const closeModal = () => {
    setModalOpen(false);
    setModalProduct(null);
  };

  /**
   * ✅ Component موحّد للكارت (محسّن لعرض أكبر قدر من البيانات)
   */
  const ProductCard = ({ product, index }) => {
    const pid = product.id;
    const isSaved = savedIds.includes(pid);
    const isSaving = savingIds.includes(pid);
    const priceInfo = getPriceInfo(product);
    const original = formatPrice(priceInfo?.price);
    const discounted = formatPrice(priceInfo?.price_after_discount);
    const originalNum = parseFloat(original || "");
    const discountedNum = parseFloat(discounted || "");
    const canShowDiscount =
      discounted &&
      Number.isFinite(originalNum) &&
      Number.isFinite(discountedNum) &&
      discountedNum > 0 &&
      discountedNum < originalNum;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }} key={pid}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: "grey.100",
            borderRadius: 3,
            overflow: "hidden",
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
            "&:hover": {
              boxShadow: "0 12px 36px rgba(213,170,47,0.14)",
              borderColor: "rgba(213,170,47,0.3)",
              transform: "translateY(-6px)",
              "& .product-image": { transform: "scale(1.07)" },
              "& .card-actions-overlay": { opacity: 1 },
            },
            animation: `fadeInUp 0.45s ease-out ${index * 0.04}s both`,
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(18px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* ── Image ── */}
          <Box sx={{ position: "relative", overflow: "hidden", bgcolor: "grey.50" }}>
            <CardActionArea onClick={() => handleHoverStart(product)} sx={{ display: "block" }}>
              <CardMedia
                component="img"
                image={getProductImage(product)}
                alt={getProductName(product)}
                className="product-image"
                sx={{
                  height: 190,
                  objectFit: "cover",
                  transition: "transform 0.45s ease",
                }}
              />
            </CardActionArea>

            {/* discount badge */}
            {canShowDiscount && (
              <Chip
                label={`-${Math.round((1 - discountedNum / originalNum) * 100)}%`}
                size="small"
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  bgcolor: "error.main",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  height: 24,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* hover action overlay */}
            <Box
              className="card-actions-overlay"
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                opacity: 0,
                transition: "opacity 0.25s ease",
                display: "flex",
                justifyContent: "center",
                gap: 1,
                pb: 1.5,
                pt: 3,
                background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.45) 100%)",
              }}
            >
              <IconButton
                onClick={() => handleSaveProduct(product)}
                disabled={isSaving}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(4px)",
                  "&:hover": { bgcolor: "#fff", transform: "scale(1.1)" },
                  transition: "all 0.2s ease",
                }}
              >
                {isSaving ? (
                  <CircularProgress size={15} sx={{ color: "primary.main" }} />
                ) : isSaved ? (
                  <FavoriteIcon sx={{ fontSize: 17, color: "error.main" }} />
                ) : (
                  <FavoriteBorderIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
              <IconButton
                onClick={() => handleOpenAddToCart(product)}
                size="small"
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  "&:hover": { bgcolor: "primary.dark", transform: "scale(1.1)" },
                  transition: "all 0.2s ease",
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Box>
          </Box>

          {/* ── Content ── */}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
            {/* Name + link */}
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
                fontSize: "0.875rem",
                mb: 0.5,
                minHeight: 38,
              }}
            >
              {getProductName(product)}
            </Typography>
            <Typography
              component={Link}
              to={`/product/${pid}`}
              onClick={(e) => e.stopPropagation()}
              variant="caption"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                textDecoration: "none",
                fontSize: "0.72rem",
                mb: 1.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {isAr ? "عرض تفاصيل المنتج ←" : "View details →"}
            </Typography>

            {/* Price */}
            <Box sx={{ mb: 1.5, minHeight: 46 }}>
              {priceInfo ? (
                canShowDiscount ? (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ textDecoration: "line-through", color: "text.disabled", fontWeight: 600, display: "block", fontSize: "0.7rem" }}
                    >
                      {original} {isAr ? "ج.م" : "EGP"}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: "primary.main", fontWeight: 800, lineHeight: 1.2 }}>
                      {discounted} {isAr ? "ج.م" : "EGP"}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="subtitle1" sx={{ color: "primary.main", fontWeight: 800 }}>
                    {original} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                )
              ) : null}
            </Box>

            {/* Sizes */}
            {(product.sizes || []).length > 0 && (
              <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap", mb: 1 }}>
                {(product.sizes || []).slice(0, 5).map((s) => (
                  <Chip
                    key={s.id || s.key}
                    label={s.size || s.key}
                    size="small"
                    sx={{
                      bgcolor: "rgba(213,170,47,0.1)",
                      color: "primary.dark",
                      fontWeight: 700,
                      height: 20,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": { px: 0.8 },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Colors */}
            {(product.colors || []).length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
                {(product.colors || []).slice(0, 8).map((c) => (
                  <Avatar
                    key={c.id || c.code}
                    sx={{
                      width: 15,
                      height: 15,
                      bgcolor: c.code || "#eee",
                      border: "1.5px solid rgba(0,0,0,0.1)",
                    }}
                    title={c.color_en || c.color_ar}
                  />
                ))}
              </Box>
            )}

            {/* Rating */}
            <Box sx={{ mt: "auto", display: "flex", alignItems: "center", gap: 0.4 }}>
              {product.reviews_summary?.average_rating ? (
                <>
                  <StarIcon sx={{ fontSize: 14, color: "primary.main" }} />
                  <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ fontSize: "0.72rem" }}>
                    {product.reviews_summary.average_rating}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                    ({product.reviews_summary.total_reviews})
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                  {isAr ? "بدون تقييم" : "No rating"}
                </Typography>
              )}
            </Box>
          </Box>
        </Card>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background:
            "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
      dir={dir}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            alignItems: "flex-start",
            textAlign: isAr ? "right" : "left",
          }}
        >
          {/* Sidebar — always on RIGHT in RTL, LEFT in LTR */}
          <Box
            sx={{
              order: { xs: -1, md: isAr ? 1 : 0 },
              width: { xs: "100%", md: 240 },
              flexShrink: 0,
              position: { md: "sticky" },
              top: 80,
              alignSelf: "flex-start",
              borderRadius: 3,
              bgcolor: "background.paper",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              overflow: "hidden",
            }}
          >
            <CategoriesHorizontallyCompo
              onSelectCategory={setSelectedCategoryId}
            />
          </Box>

          {/* Main */}
          <Box sx={{ flex: 1, minWidth: 0, order: { xs: 0, md: isAr ? 0 : 1 } }}>
            <Box
              sx={{
                mb: 3,
                p: { xs: 1.5, md: 2 },
                borderRadius: 3,
                bgcolor: "background.paper",
                border: "1px solid rgba(213, 170, 47, 0.12)",
                boxShadow: "0 10px 30px rgba(17, 24, 39, 0.05)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    isAr ? "ابحث عن منتج أو وصف المنتج..." : "Search products or descriptions..."
                  }
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "primary.main" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: "100%", md: 280 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 999,
                      bgcolor: "rgba(245, 247, 250, 0.9)",
                      transition: "all 0.25s ease",
                      "& fieldset": {
                        borderColor: "rgba(213, 170, 47, 0.22)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(213, 170, 47, 0.45)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(213, 170, 47, 0.12)",
                      },
                    },
                  }}
                />

                {searchQuery && (
                  <Button
                    onClick={clearSearch}
                    variant="text"
                    sx={{
                      minWidth: "fit-content",
                      px: 1.5,
                      fontWeight: 700,
                      color: "text.secondary",
                    }}
                  >
                    {isAr ? "مسح البحث" : "Clear"}
                  </Button>
                )}
              </Box>
            </Box>

            {selectedCategoryId || (searchQuery && searchQuery.trim()) ? (
              <>
                {/* Header */}
                <Box
                  sx={{
                    mb: 3,
                    pb: 2,
                    borderBottom: "2px solid",
                    borderImage:
                      "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      background:
                        "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      mb: 0.5,
                    }}
                    align={isAr ? "right" : "left"}
                  >
                    {qTerm
                      ? isAr
                        ? `نتائج البحث عن: "${searchQuery}"`
                        : `Search results for: "${searchQuery}"`
                      : matchedProducts && matchedProducts.length > 0
                        ? isAr
                          ? matchedProducts[0].categroy?.name_ar ||
                            matchedProducts[0].categroy?.name_en
                          : matchedProducts[0].categroy?.name_en ||
                            matchedProducts[0].categroy?.name_ar
                        : t("filtered_products", {
                            defaultValue: isAr
                              ? "منتجات الفئة"
                              : "Category products",
                          })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {matchedProducts?.length || 0} {isAr ? "منتج" : "products"}
                  </Typography>
                </Box>

                {matchedProducts && matchedProducts.length > 0 ? (
                  <Grid container spacing={2} alignItems="stretch">
                    {matchedProducts.map((product, index) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        index={index}
                      />
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {t("no_products_found", {
                        defaultValue: isAr
                          ? "لا توجد منتجات"
                          : "No products found",
                      })}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                {Object.keys(groups).length === 0 && (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {t("no_products_found", {
                        defaultValue: isAr
                          ? "لا توجد منتجات"
                          : "No products found",
                      })}
                    </Typography>
                  </Box>
                )}

                {Object.entries(groups).map(([groupKey, items]) => {
                  const list = Array.isArray(items) ? items : [];
                  if (list.length === 0) return null;
                  const first = list[0];

                  return (
                    <Box key={groupKey} sx={{ mb: 5 }}>
                      <Box
                        sx={{
                          mb: 3,
                          pb: 2,
                          borderBottom: "2px solid",
                          borderImage:
                            "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
                        }}
                      >
                        <Typography
                          variant="h5"
                          fontWeight={800}
                          sx={{
                            background:
                              "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            display: "inline-block",
                          }}
                          align={isAr ? "right" : "left"}
                        >
                          {getCategoryLabel(groupKey, first)}
                        </Typography>
                      </Box>

                      <Grid container spacing={2} alignItems="stretch">
                        {list.map((product, index) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            index={index}
                          />
                        ))}
                      </Grid>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </Box>
      </Container>

      {/* Dialog (كما هو عندك) */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          onMouseLeave: closeModal,
        }}
      >
        <DialogTitle>
          {t("product_details", "تفاصيل المنتج")}
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ProductDialog
            product={modalProduct}
            open={modalOpen}
            onClose={closeModal}
          />
        </DialogContent>
      </Dialog>

      <AddToCartDialog
        product={addCartProduct}
        sizes={addCartProduct?.sizes || []}
        colors={addCartProduct?.colors || []}
        open={addCartOpen}
        onClose={handleCloseAddToCart}
        onAdded={() => {}}
      />
    </Box>
  );
};

export default Products;
